/**
 * Builds the URL of the standalone export viewer shell (public/export.html).
 *
 * Every "Export PDF / PPTX" and "Open HTML preview" link points at the
 * shell rather than straight at the API endpoint. The shell shows a
 * branded loading animation while the artefact generates, then embeds it
 * under a Back + breadcrumb header — so a user who taps "Export" on a
 * phone always has a way back into the app and never stares at a blank
 * tab while the server renders.
 *
 * `src` is the same-origin API path that actually produces the artefact
 * (the shell validates it is a single-slash absolute path before
 * fetching it). `back` defaults to the current in-app route so the
 * shell's Back button returns to the deck/memo it was opened from.
 */
export type ExportKind = 'pdf' | 'pptx' | 'html';

export function exportShellUrl(opts: {
  src: string;
  kind: ExportKind;
  title?: string;
  back?: string;
}): string {
  const params = new URLSearchParams();
  params.set('src', opts.src);
  params.set('kind', opts.kind);
  if (opts.title) params.set('title', opts.title);
  const back = opts.back ?? (typeof window !== 'undefined'
    ? window.location.pathname + window.location.hash
    : '');
  if (back) params.set('back', back);
  return `/export.html?${params.toString()}`;
}

/** Append ?stream=1 (or &stream=1) to an export endpoint URL so it returns
 *  an NDJSON progress stream instead of a 302 redirect. Used by the in-place
 *  (no page-change) export buttons that show a live progress label. */
export function withStream(url: string): string {
  return url + (url.indexOf('?') === -1 ? '?' : '&') + 'stream=1';
}
